import { ErrorMessage, Form, Formik } from 'formik';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

import Button from '@/components/ui/button';
import { ProfileCancelIcon } from '@/components/ui/svg/ProfileIcons';
import {
	checkDateTimeValid,
	formatTimestamp,
	reduceHoursFromDate,
	sanitizeValuesObject,
} from '@/components/utils/utils';
import { useUser } from '@/hooks/useUser';
import { AuthRoutes } from '@/interfaces/auth/AuthRoutes.enum';
import { HaggleEmailType } from '@/interfaces/notifications/Notifications';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

interface NotificationRequiredInfo {
	haggleStartDate?: string;
	haggleEventId?: number;
	productId: number;
	productName: string;
	productImage: string;
	productUrl: string;
	sent: false;
}

const GetNotifiedModal = ({
	defaultClose,
	notificationRequiredInfo,
}: {
	defaultClose: () => void;
	notificationRequiredInfo: NotificationRequiredInfo | null;
}) => {
	const { user } = useUser();
	const [loading, setLoading] = useState<boolean>(false);

	const handleHourChange = (
		event: React.ChangeEvent<HTMLInputElement>,
		handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
	) => {
		let value = Number(event.target.value);
		if (value < 1) value = 1;
		if (value > 24) value = 24;

		handleChange({
			target: { name: event.target.name, value: value.toString() },
		} as React.ChangeEvent<HTMLInputElement>);
	};

	const createNotification = (values: {
		username: string;
		notificationTime: number;
	}) => {
		const sanitizedValues = sanitizeValuesObject(values);

		if (
			notificationRequiredInfo &&
			notificationRequiredInfo?.haggleStartDate &&
			notificationRequiredInfo.haggleEventId
		) {
			const haggleStartDate = new Date(
				notificationRequiredInfo.haggleStartDate,
			);
			const selectedHours = Number(sanitizedValues.notificationTime);

			if (haggleStartDate && !isNaN(selectedHours)) {
				const sendAt = reduceHoursFromDate(selectedHours, haggleStartDate);
				const checkIfTimeHasPassed = checkDateTimeValid(sendAt);
				if (checkIfTimeHasPassed) {
					const sendAtDateTime = formatTimestamp(new Date(sendAt));
					notificationService.error(
						'Scheduled time: ' +
							sendAtDateTime.date +
							' ' +
							sendAtDateTime.time +
							'. The selected time has already passed. Please choose a valid time.',
					);
					defaultClose();
					return;
				}
				haggleApiService
					.createNotification(
						values.username,
						HaggleEmailType.HaggleNotificationReminderEmail,
						false,
						notificationRequiredInfo.haggleStartDate,
						sendAt,
						notificationRequiredInfo.haggleEventId,
						notificationRequiredInfo.productId.toString(),
						notificationRequiredInfo.productName,
						notificationRequiredInfo.productUrl,
						notificationRequiredInfo.productImage,
					)
					.then(() => {
						notificationService.success(
							'You will be notified via email before the haggle starts!',
						);
						defaultClose();
					})
					.catch((error) => {
						notificationService.error(error.message);
						defaultClose();
					})
					.finally(() => {
						setLoading(false);
					});
			} else {
				setLoading(false);
				notificationService.error(
					'Something went wrong, please try again later!',
				);
				defaultClose();
			}
		} else {
			setLoading(false);
			notificationService.error(
				'Something went wrong, please try again later!',
			);
			defaultClose();
		}
	};

	return (
		<div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
			<div className="p-6 bg-white rounded-lg shadow-lg max-w-xl w-full relative">
				<button className="absolute top-3 right-3" onClick={defaultClose}>
					<ProfileCancelIcon />
				</button>
				<div className="flex flex-col justify-center items-center">
					<h2 className="text-lg font-semibold text-center my-6">
						Don't miss out! Get notified about this Haggle Event!
					</h2>
					<Formik
						initialValues={{
							username: user?.username || '',
							notificationTime: 1,
						}}
						validationSchema={Yup.object({
							username: Yup.string()
								.matches(
									/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
									'Enter a valid email address',
								)
								.email('Invalid email')
								.required('Email is required'),
							notificationTime: Yup.number()
								.min(1, 'Minimum 1 hour')
								.max(24, 'Maximum 24 hours')
								.required('Pick a time'),
						})}
						onSubmit={(values) => {
							setLoading(true);
							createNotification(values);
						}}
					>
						{({ values, handleChange }) => (
							<Form className="flex flex-col gap-4 w-full px-20">
								<div>
									<label className="block text-sm font-medium">
										Email Address
									</label>
									<input
										type="email"
										name="username"
										placeholder="email@example.com"
										className="w-full mt-1 p-2 border rounded-md"
										value={values.username}
										onChange={handleChange}
									/>
									<ErrorMessage
										name="username"
										component="div"
										className="text-red-500 text-sm mt-1"
									/>
								</div>

								<div className="flex items-center gap-2">
									<label className="text-sm font-medium">Notify me</label>
									<input
										type="number"
										min={1}
										max={24}
										name="notificationTime"
										value={values.notificationTime}
										onChange={(e) => handleHourChange(e, handleChange)}
										className="w-16 text-center p-2 border rounded-md"
									/>
									<span className="text-sm font-medium">
										{' '}
										hours before the event starts
									</span>
									<ErrorMessage
										name="notificationTime"
										component="div"
										className="text-red-500 text-sm ml-2"
									/>
								</div>

								<div className="flex justify-center">
									<button
										disabled={loading}
										type="submit"
										className="px-10 py-3 text-center text-sm font-medium bg-black text-white rounded-md hover:bg-gray-900"
									>
										Email Me
									</button>
								</div>
							</Form>
						)}
					</Formik>
					{user?.username && (
						<p className="text-center text-sm mt-4">
							Join <span className="font-bold">Haggle</span> and discover
							amazing deals.
						</p>
					)}
					<Link to={AuthRoutes.SignUpConsumer}>
						<Button
							transition="none"
							className="w-full text-white bg-purpleBrand-500 hover:bg-purpleBrand-600 rounded-md active:bg-purpleBrand-900 mt-4  "
						>
							Sign Up here
						</Button>
					</Link>
				</div>
			</div>
		</div>
	);
};

export default GetNotifiedModal;
